const NAMED_COLORS = {
  black: '#000000',
  white: '#ffffff',
  snow: '#fffafa',
  transparent: '#00000000',
};

const clampByte = (value) => Math.min(255, Math.max(0, Number(value) || 0));

export const parseCssColor = (input) => {
  if (typeof input !== 'string') return null;

  const normalized = input.trim().toLowerCase();
  const color = NAMED_COLORS[normalized] || normalized;

  if (/^#[0-9a-f]{3,4}$/i.test(color)) {
    const values = color.slice(1).split('').map((value) => parseInt(value + value, 16));
    return {
      r: values[0],
      g: values[1],
      b: values[2],
      a: values.length === 4 ? values[3] / 255 : 1,
    };
  }

  if (/^#[0-9a-f]{6}([0-9a-f]{2})?$/i.test(color)) {
    return {
      r: parseInt(color.slice(1, 3), 16),
      g: parseInt(color.slice(3, 5), 16),
      b: parseInt(color.slice(5, 7), 16),
      a: color.length === 9 ? parseInt(color.slice(7, 9), 16) / 255 : 1,
    };
  }

  const rgbMatch = color.match(/^rgba?\(([^)]+)\)$/i);
  if (!rgbMatch) return null;

  const values = rgbMatch[1].split(',').map((value) => value.trim());
  if (values.length < 3) return null;

  const readChannel = (value) => (
    value.endsWith('%')
      ? clampByte((parseFloat(value) / 100) * 255)
      : clampByte(parseFloat(value))
  );

  return {
    r: readChannel(values[0]),
    g: readChannel(values[1]),
    b: readChannel(values[2]),
    a: values[3] === undefined ? 1 : Math.min(1, Math.max(0, parseFloat(values[3]) || 0)),
  };
};

const compositeColor = (foreground, background) => {
  const alpha = foreground.a ?? 1;
  return {
    r: foreground.r * alpha + background.r * (1 - alpha),
    g: foreground.g * alpha + background.g * (1 - alpha),
    b: foreground.b * alpha + background.b * (1 - alpha),
    a: 1,
  };
};

const relativeLuminance = ({ r, g, b }) => {
  const channels = [r, g, b].map((channel) => {
    const value = channel / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });
  return channels[0] * 0.2126 + channels[1] * 0.7152 + channels[2] * 0.0722;
};

export const getContrastRatio = (foregroundInput, backgroundInput) => {
  const foreground = parseCssColor(foregroundInput);
  const background = parseCssColor(backgroundInput);
  if (!foreground || !background) return 0;

  const opaqueBackground = background.a < 1
    ? compositeColor(background, parseCssColor('#ffffff'))
    : background;
  const opaqueForeground = foreground.a < 1
    ? compositeColor(foreground, opaqueBackground)
    : foreground;
  const foregroundLuminance = relativeLuminance(opaqueForeground);
  const backgroundLuminance = relativeLuminance(opaqueBackground);
  const lighter = Math.max(foregroundLuminance, backgroundLuminance);
  const darker = Math.min(foregroundLuminance, backgroundLuminance);
  return (lighter + 0.05) / (darker + 0.05);
};

export const chooseReadableTextColor = (
  backgrounds,
  preferredColor,
  minimumRatio = 4.5
) => {
  const validBackgrounds = (Array.isArray(backgrounds) ? backgrounds : [backgrounds])
    .filter((background) => parseCssColor(background));

  if (!validBackgrounds.length) return preferredColor || '#ffffff';

  const candidates = [preferredColor, '#ffffff', '#0f172a']
    .filter((color, index, list) => parseCssColor(color) && list.indexOf(color) === index);
  const score = (color) => Math.min(
    ...validBackgrounds.map((background) => getContrastRatio(color, background))
  );

  if (preferredColor && score(preferredColor) >= minimumRatio) return preferredColor;
  return candidates.reduce((best, candidate) => (
    score(candidate) > score(best) ? candidate : best
  ), candidates[0]);
};
